import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import type { CommentBlock } from "@core-ui/CommentBlock";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import { getEditorStore } from "@core-ui/stores/EditorStore";
import useCurrentAuthor from "@ext/markdown/elements/comment/edit/logic/hooks/useCurrentAuthor";
import { addComment, deleteComment } from "@ext/markdown/elements/comment/edit/logic/stores/CommentsStore";
import { addReviewItem, deleteReviewItem } from "@ext/review/logic/store/ReviewStore";
import { resolveCommentDOMSelector } from "@ext/review/logic/utils/resolveCommentDOMSelector";
import type { CommentReviewListItem } from "@ext/review/models/ReviewList";
import type { Range } from "@tiptap/core";
import { type RefObject, useCallback, useRef } from "react";

const useCommentCallbacks = (articlePropsRef: RefObject<ClientArticleProps>) => {
	const author = useCurrentAuthor();
	const apiUrlCreator = ApiUrlCreator.value;
	const pendingPersistenceRef = useRef(new Map<string, Promise<unknown>>());

	const enqueueCommentPersistence = useCallback(<T>(id: string, operation: () => Promise<T>) => {
		const pendingPersistence = pendingPersistenceRef.current;
		const previous = pendingPersistence.get(id);
		const current = previous ? previous.catch(() => undefined).then(operation) : operation();
		const clear = () => {
			if (pendingPersistence.get(id) === current) pendingPersistence.delete(id);
		};

		pendingPersistence.set(id, current);
		void current.then(clear, clear);
		return current;
	}, []);

	const loadComment = useCallback(
		async (id: string) => {
			const url = apiUrlCreator.getComment(id, articlePropsRef.current?.ref?.path);
			const res = await FetchService.fetch<CommentBlock>(url);
			if (!res.ok) return;

			const comment = await res.json();
			return comment;
		},
		[articlePropsRef],
	);

	const toReviewItem = useCallback(
		(id: string, comment: CommentBlock): CommentReviewListItem => ({
			id,
			type: "comments",
			pathname: articlePropsRef.current.pathname,
			selector: resolveCommentDOMSelector(id),
			date: comment?.comment?.dateTime ?? new Date().toISOString(),
			author: {
				email: comment?.comment?.user?.mail ?? author.mail,
				name: comment?.comment?.user?.name ?? author.name,
			},
			commentBlock: comment,
		}),
		[articlePropsRef, author],
	);

	const onCommentSaved = useCallback(
		(id: string, comment: CommentBlock) => {
			addComment(articlePropsRef.current.pathname, author, id, articlePropsRef.current.title);
			addReviewItem(toReviewItem(id, comment));
		},
		[articlePropsRef, toReviewItem, author],
	);

	const onMarkAdded = useCallback(
		async (id: string) => {
			const editor = getEditorStore().editor;
			if (!editor || editor.isDestroyed) return;

			const storage = editor.storage.comment;
			if (storage.comments.has(id)) return;

			const data: CommentBlock = storage.deleted.get(id);
			if (!data) return;

			storage.comments.set(id, data);
			storage.deleted.delete(id);

			addComment(articlePropsRef.current.pathname, data.comment.user, id, articlePropsRef.current.title);
			addReviewItem(toReviewItem(id, data));

			const url = apiUrlCreator.updateComment(id, articlePropsRef.current?.ref?.path);
			const body = JSON.stringify(data);
			const res = await enqueueCommentPersistence(id, () => FetchService.fetch(url, body));
			if (res.ok) return;

			deleteComment(articlePropsRef.current.pathname, data.comment.user, id);
			deleteReviewItem(id);
		},
		[articlePropsRef, enqueueCommentPersistence, toReviewItem],
	);

	const onMarkDeleted = useCallback(
		async (id: string, positions: Range[]) => {
			const editor = getEditorStore().editor;
			if (!editor || editor.isDestroyed) return;

			const storage = editor.storage.comment;
			const data: CommentBlock = storage.comments.get(id) ?? (await loadComment(id));
			if (data) {
				storage.deleted.set(id, data);
				storage.comments.delete(id);
			}

			if (positions.length) return;

			const user = data?.comment?.user ?? author;
			const url = apiUrlCreator.deleteComment(id, articlePropsRef.current?.ref?.path);
			const res = await enqueueCommentPersistence(id, () => FetchService.fetch(url));
			if (!res.ok || storage.comments.has(id)) return;

			deleteComment(articlePropsRef.current.pathname, user, id);
			deleteReviewItem(id);
		},
		[loadComment, articlePropsRef, author, enqueueCommentPersistence],
	);

	return {
		onMarkAdded,
		onMarkDeleted,
		onCommentSaved,
	};
};

export default useCommentCallbacks;
